import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';

import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import GenericSelect from '../../../components/ui/GenericSelect';
import TextInput from '../../../components/ui/TextInput';
import CheckboxInput from '../../../components/ui/CheckboxInput';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import Tooltip from '../../../components/ui/Tooltip';

const Card = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 24px;
  margin-bottom: 16px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
`;

const InfoLabel = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 4px;
`;

const InfoValue = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 500;
`;

const PackageTitle = styled.h2`
  margin: 0 0 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 22px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const PackageSubtitle = styled.p`
  margin: 0 0 16px;
  color: #6b7280;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
`;

const SectionTitle = styled.h3`
  margin: 0 0 16px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  th, td {
    padding: 10px 12px;
    text-align: left;
    border-bottom: 1px solid #e5e7eb;
  }
  th {
    background: #f9fafb;
    font-weight: 600;
    font-size: 13px;
    color: #6b7280;
  }
  tr:last-child td { border-bottom: none; }
`;

const RowForm = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr auto;
  gap: 12px;
  align-items: end;
  padding: 12px;
  background: #f9fafb;
  border-radius: 6px;
  margin-top: 12px;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  background: ${({ $variant, theme }) =>
    $variant === 'danger' ? '#FEE2E2' : `${theme.colors.primary.main}14`};
  color: ${({ $variant, theme }) =>
    $variant === 'danger' ? '#991B1B' : theme.colors.primary.main};

  &:hover:not(:disabled) {
    background: ${({ $variant, theme }) =>
      $variant === 'danger' ? '#FCA5A5' : `${theme.colors.primary.main}28`};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export default function GeneralTab({ pkg, onPackageChanged }) {
  const { t } = useTranslation();
  const { canWrite } = useAuth();

  const [benefits, setBenefits] = useState([]);
  const [saving, setSaving] = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState({ isOpen: false, itemId: null });
  const [newItem, setNewItem] = useState({ benefitId: '', isMandatory: false, minimumLimit: '', overrideCompanyCost: '' });

  useEffect(() => {
    api.get('/v1/benefits?pageSize=1000')
      .then((res) => setBenefits(res.data?.data?.data || []))
      .catch(() => {});
  }, []);

  const persist = useCallback(async (items) => {
    if (!pkg) return false;
    setSaving(true);
    try {
      await api.put(`/v1/benefit-packages/${pkg.id}`, {
        name: pkg.name,
        description: pkg.description,
        targetPositionId: pkg.targetPositionId,
        targetDepartmentId: pkg.targetDepartmentId,
        targetEmploymentType: pkg.targetEmploymentType,
        effectiveStartDate: pkg.effectiveStartDate,
        effectiveEndDate: pkg.effectiveEndDate,
        cafeteriaAnnualBudget: pkg.cafeteriaAnnualBudget,
        items,
      });
      await onPackageChanged();
      return true;
    } catch {
      return false;
    } finally {
      setSaving(false);
    }
  }, [pkg, onPackageChanged]);

  const handleAddItem = useCallback(async () => {
    if (!newItem.benefitId) return;
    const items = [
      ...(pkg?.items || []).map((it) => ({
        benefitId: it.benefitId,
        isMandatory: it.isMandatory,
        minimumLimit: it.minimumLimit,
        overrideCompanyCost: it.overrideCompanyCost,
      })),
      {
        benefitId: newItem.benefitId,
        isMandatory: newItem.isMandatory,
        minimumLimit: newItem.minimumLimit ? Number(newItem.minimumLimit) : null,
        overrideCompanyCost: newItem.overrideCompanyCost ? Number(newItem.overrideCompanyCost) : null,
      },
    ];
    const ok = await persist(items);
    if (ok) setNewItem({ benefitId: '', isMandatory: false, minimumLimit: '', overrideCompanyCost: '' });
  }, [newItem, pkg, persist]);

  const handleRemoveItem = useCallback(async () => {
    if (!removeConfirm.itemId) return;
    const items = (pkg?.items || [])
      .filter((it) => it.id !== removeConfirm.itemId)
      .map((it) => ({
        benefitId: it.benefitId,
        isMandatory: it.isMandatory,
        minimumLimit: it.minimumLimit,
        overrideCompanyCost: it.overrideCompanyCost,
      }));
    await persist(items);
    setRemoveConfirm({ isOpen: false, itemId: null });
  }, [pkg, persist, removeConfirm]);

  const availableBenefits = useMemo(() => {
    const usedIds = new Set((pkg?.items || []).map((it) => it.benefitId));
    return benefits.filter((b) => !usedIds.has(b.id));
  }, [benefits, pkg]);

  if (!pkg) return null;

  return (
    <div>
      <Card>
        <PackageTitle>{pkg.name}</PackageTitle>
        {pkg.description && <PackageSubtitle>{pkg.description}</PackageSubtitle>}
        <InfoGrid>
          <div>
            <InfoLabel>{t('benefitPackage.targetPosition')}</InfoLabel>
            <InfoValue>{pkg.targetPositionName || '-'}</InfoValue>
          </div>
          <div>
            <InfoLabel>{t('benefitPackage.targetDepartment')}</InfoLabel>
            <InfoValue>{pkg.targetDepartmentName || '-'}</InfoValue>
          </div>
          <div>
            <InfoLabel>{t('benefitPackage.effectiveStartDate')}</InfoLabel>
            <InfoValue>{pkg.effectiveStartDate ? new Date(pkg.effectiveStartDate).toLocaleDateString('tr-TR') : '-'}</InfoValue>
          </div>
          <div>
            <InfoLabel>{t('benefitPackage.effectiveEndDate')}</InfoLabel>
            <InfoValue>{pkg.effectiveEndDate ? new Date(pkg.effectiveEndDate).toLocaleDateString('tr-TR') : '-'}</InfoValue>
          </div>
          <div>
            <InfoLabel>{t('benefitPackage.totalMonthlyCost')}</InfoLabel>
            <InfoValue>{Number(pkg.totalMonthlyCost ?? 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</InfoValue>
          </div>
          <div>
            <InfoLabel>{t('benefitPackage.cafeteriaAnnualBudget')}</InfoLabel>
            <InfoValue>{pkg.cafeteriaAnnualBudget != null ? `${Number(pkg.cafeteriaAnnualBudget).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺` : '-'}</InfoValue>
          </div>
          <div>
            <InfoLabel>{t('benefitPackage.assignedEmployeeCount')}</InfoLabel>
            <InfoValue>{pkg.assignedEmployeeCount ?? 0}</InfoValue>
          </div>
        </InfoGrid>
      </Card>

      <Card>
        <SectionTitle>{t('benefitPackage.itemsTitle')}</SectionTitle>

        <Table>
          <thead>
            <tr>
              <th>{t('benefitPackageItem.benefit')}</th>
              <th>{t('benefitPackageItem.category')}</th>
              <th>{t('benefitPackageItem.isMandatory')}</th>
              <th>{t('benefitPackageItem.minimumLimit')}</th>
              <th>{t('benefitPackageItem.effectiveCost')}</th>
              {canWrite('BN') && <th></th>}
            </tr>
          </thead>
          <tbody>
            {(pkg.items || []).length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: '#9ca3af', padding: 24 }}>{t('benefitPackage.noItems')}</td></tr>
            )}
            {(pkg.items || []).map((it) => (
              <tr key={it.id}>
                <td>{it.benefitName}</td>
                <td>{it.categoryName}</td>
                <td>{it.isMandatory ? t('common.yes') : t('common.no')}</td>
                <td>{it.minimumLimit != null ? Number(it.minimumLimit).toLocaleString('tr-TR') : '-'}</td>
                <td>{Number(it.effectiveMonthlyCost ?? 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</td>
                {canWrite('BN') && (
                  <td style={{ textAlign: 'right' }}>
                    <Tooltip text={t('common.remove')} position="left">
                      <IconButton
                        $variant="danger"
                        onClick={() => setRemoveConfirm({ isOpen: true, itemId: it.id })}
                        disabled={saving}
                        aria-label={t('common.remove')}>
                        <Trash2 size={16} />
                      </IconButton>
                    </Tooltip>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>

        {canWrite('BN') && availableBenefits.length > 0 && (
          <RowForm>
            <GenericSelect
              label={t('benefitPackageItem.benefit')}
              value={newItem.benefitId}
              onChange={(v) => setNewItem({ ...newItem, benefitId: v })}
              options={availableBenefits}
              idKey="id"
              labelKey="name"
            />
            <CheckboxInput
              label={t('benefitPackageItem.isMandatory')}
              checked={newItem.isMandatory}
              onChange={(checked) => setNewItem({ ...newItem, isMandatory: checked })}
            />
            <TextInput
              type="number"
              label={t('benefitPackageItem.minimumLimit')}
              value={newItem.minimumLimit}
              onChange={(e) => setNewItem({ ...newItem, minimumLimit: e.target.value })}
            />
            <TextInput
              type="number"
              label={t('benefitPackageItem.overrideCompanyCost')}
              value={newItem.overrideCompanyCost}
              onChange={(e) => setNewItem({ ...newItem, overrideCompanyCost: e.target.value })}
            />
            <Tooltip text={t('common.add')} position="left">
              <IconButton
                onClick={handleAddItem}
                disabled={!newItem.benefitId || saving}
                aria-label={t('common.add')}>
                <Plus size={18} />
              </IconButton>
            </Tooltip>
          </RowForm>
        )}
      </Card>

      <ConfirmDialog
        isOpen={removeConfirm.isOpen}
        title={t('benefitPackage.removeItemTitle')}
        message={t('benefitPackage.removeItemMessage')}
        onConfirm={handleRemoveItem}
        onCancel={() => setRemoveConfirm({ isOpen: false, itemId: null })}
        loading={saving}
      />
    </div>
  );
}
